import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Icon } from "@blueprintjs/core";
import './ActivitiesList.css';
import { ListingTable } from "../listing";
import moment from "moment";
import { DateToLocal } from "components/dates/DateToLocal";
import { FormCheckbox } from "components/form-fields";
import './HarshBrakingPanel.css';
import './InfoPanel.css';

const defaultArray = [];

export function ActivitiesList(
    {
        id = "activities-list",
        activitiesList = defaultArray,
        isLoading = false,
        selectable = false,
        onActivitySelected = () => {},
        onActivityCheckChange = () => {},
        selectedActivities = defaultArray,
        selectAll = () => {},
        rowHighlighting = true,
        highlightedActivity
    }
) {

    const [mappedActivityRows, setMappedActivityRows] = useState([]);
    const [mappedActivityRowClasses, setMappedActivityRowClasses] = useState([]);
    const [selectedRowIndex, setSelectedRowIndex] = useState();
    const selectedActivitiesIdPrefix = "act-list-";
    const [selectAllValue, setSelectAll] = useState(false)   

    const activityTypeMappings = [{ typeId: 0, icon: "break.png", name: "Rest" }, { typeId: 1, icon: "available.png", name: "Available" }, { typeId: 2, icon: "work.png", name: "Work" }, { typeId: 3, icon: "driving.png ", name: "Driving" }];
    const scrollTo = useRef();

    let columnHeaders = [<div key="act-list-colour-header"></div>, "Activity Type", "Time", "Date", "Length"];

    if (selectable) {
        columnHeaders.push(<div key="act-list-select-all" className="form-field">Select All <FormCheckbox checked={selectAllValue} disabled={isLoading} onChange={(x) => toggleSelectAll(x)} /></div>);
    }

    useEffect(() => {
        if(selectedActivities.length !== activitiesList.length) {
            setSelectAll(false);
        }
        
        mapRows(activitiesList, selectedRowIndex);
    }, [selectedRowIndex, selectedActivities]);

    useEffect(() => {
        if (highlightedActivity) {
            let activityIndex = activitiesList.findIndex((a) => a.startDate === highlightedActivity.startDate);

            if (activityIndex >= 0 && activityIndex !== selectedRowIndex) {
                setSelectedRowIndex(activityIndex);

                setTimeout(() => {
                    if (scrollTo.current) {
                        scrollTo.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                } , 100);
            }
        }
    }, [highlightedActivity]);

    function toggleSelectAll(event) {
        setSelectAll(event.target.checked);
        selectAll(event.target.checked)
    }

    function toggleSelectRow(e) {
        let {id: idValue, checked} = e.target;
        idValue = idValue.substring(selectedActivitiesIdPrefix.length);
        onActivityCheckChange(idValue, checked);
    }

    function onRowClick(rowIndex) {
        setSelectedRowIndex(rowIndex);
        onActivitySelected(activitiesList[rowIndex]);
    }

    function mapRows(activities, highlightedIndex)
    {
        let rows = [];
        let rowClasses= [];
        for (let i = 0; i < activities.length; i++) {

            const activityType = activityTypeMappings.find((m) => m.typeId === activities[i].typeId);
            let cols = [
                <div key={moment(activities[i].startDate).format("MMDDHHmm")} className="listing-left-bar" style={{ backgroundColor: activities[i].bgColor }} ref={i === highlightedIndex ? scrollTo : null}></div>,
                <><div className="activity-list-icon"><Icon icon={<img src={`/icons/activity-types/${activityType.icon}`} className="vu-activity-icon" alt={activityType.name} />} size={12} /></div><div className="activity-list-name">{activityType.name}</div></>,
                <DateToLocal key={`${i}-time`} format="HH:mm">{activities[i].startDate}</DateToLocal>,
                <DateToLocal key={`${i}-date`} format="DD MMM YYYY">{activities[i].startDate}</DateToLocal>,
                `${Math.floor(activities[i].duration / 60)}h ${activities[i].duration % 60}m`
            ];
            if (selectable) {
               cols.push(<div className="form-field"><FormCheckbox key={`${selectedActivitiesIdPrefix}${activities[i].activityId}-checkbox`} id={`${selectedActivitiesIdPrefix}${activities[i].activityId}`} checked={selectedActivities.includes(activities[i].activityId.toString())} onChange={(x) => toggleSelectRow(x)} /></div>);
            }
            rows.push(cols);
            
            // Determine if this row is highlighted or starts a new activity group
            let rowClass = "";
            if (i === highlightedIndex && rowHighlighting) {
                rowClass = "activity-list-highlighted";
            }
            // Add spacing class if this activity has a different color from the previous activity
            if (i > 0 && activities[i].bgColor !== activities[i - 1].bgColor) {
                rowClass += (rowClass ? " " : "") + "activity-group-start";
            }
            rowClasses.push(rowClass || null);
        }
        setMappedActivityRows(rows);
        setMappedActivityRowClasses(rowClasses);
    }

    useEffect(() => {
        mapRows(activitiesList);
    }, [activitiesList]);

    return (
        <div className="activities-list">
            <ListingTable 
                id={id}
                headers={columnHeaders}
                data={mappedActivityRows}
                columnClasses={["listing-left-bar", "activities-listing", "activities-listing", "activities-listing", "activities-listing", "activities-listing-checkbox"]}
                loadingData={isLoading}
                totalRecordCount={mappedActivityRows.length}
                pageable={false}
                allowTableScrollVertical={true}
                rowClickEnabled={true}
                onRowClick={onRowClick}
                rowClasses={mappedActivityRowClasses}
                noDataHeading="No activities"
            />
        </div>
    );
}

ActivitiesList.propTypes = {
    id: PropTypes.string,
    activitiesList: PropTypes.array,
    isLoading: PropTypes.bool,
    selectable: PropTypes.bool,
    onActivitySelected: PropTypes.func,
    onActivityCheckChange: PropTypes.func,
    selectedActivities: PropTypes.array,
    selectAll: PropTypes.func,
    rowHighlighting: PropTypes.bool,
    highlightedActivity: PropTypes.object
};